import axios from 'axios';

// Detectar automáticamente la URL del backend basado en el dominio actual.
// Los hosts de preview y el origen canónico se pueden cambiar en el build
// (REACT_APP_CANONICAL_ORIGIN, REACT_APP_PREVIEW_REDIRECT_HOSTS,
// REACT_APP_PREVIEW_API_HOSTS). Los valores por defecto son los de Emergent,
// así el despliegue actual sigue igual.
const CANONICAL_ORIGIN = (process.env.REACT_APP_CANONICAL_ORIGIN || 'https://feconciencia.com').replace(/\/+$/, '');
const PREVIEW_REDIRECT_HOSTS = (process.env.REACT_APP_PREVIEW_REDIRECT_HOSTS || 'emergent.host')
  .split(',').map((s) => s.trim()).filter(Boolean);
const PREVIEW_API_HOSTS = (process.env.REACT_APP_PREVIEW_API_HOSTS || 'preview.emergentagent.com,emergentagent.com')
  .split(',').map((s) => s.trim()).filter(Boolean);

const hostMatches = (hostname, suffixes) => suffixes.some((suffix) => suffix && hostname.includes(suffix));

const getBackendUrl = () => {
  // Si estamos en el navegador
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;
    
    // Redirect desde despliegues viejos (por defecto *.emergent.host) al origen canónico
    if (hostMatches(hostname, PREVIEW_REDIRECT_HOSTS) && !hostname.includes('feconciencia.com')) {
      window.location.href = CANONICAL_ORIGIN + window.location.pathname + window.location.search;
      return CANONICAL_ORIGIN + '/api';
    }
    
    // Producción (feconciencia.com)
    if (hostname === 'feconciencia.com' || hostname === 'www.feconciencia.com') {
      return CANONICAL_ORIGIN + '/api';
    }
    
    // Hosts de preview que sirven API en el mismo origen (por defecto Emergent)
    if (hostMatches(hostname, PREVIEW_API_HOSTS)) {
      return `${protocol}//${hostname}/api`;
    }
    
    // Any other domain - use relative path
    if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
      return `${protocol}//${hostname}/api`;
    }
  }
  
  // Desarrollo local o fallback a la variable de entorno
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  if (envUrl) {
    return envUrl + '/api';
  }
  
  // Fallback absoluto
  return '/api';
};

// Backend ROOT (without /api suffix) — used by audio/video components that build their own paths
export const getBackendRoot = () => {
  const apiUrl = getBackendUrl();
  return apiUrl.endsWith('/api') ? apiUrl.slice(0, -4) : apiUrl;
};

const API_URL = getBackendUrl();
console.log('[API] Backend URL:', API_URL);

// Flag to prevent redirect loops during initialization
let isInitializing = true;
let initializationTimeout = null;
let isLoggingOut = false;

// Mark initialization as complete after a short delay
const markInitialized = () => {
  if (initializationTimeout) clearTimeout(initializationTimeout);
  initializationTimeout = setTimeout(() => {
    isInitializing = false;
  }, 2000);
};

// Call this once the app is ready
export const completeInitialization = () => {
  isInitializing = false;
  if (initializationTimeout) clearTimeout(initializationTimeout);
};

// Call this when logging out to prevent 401 redirects
export const setLoggingOut = (value) => {
  isLoggingOut = value;
};

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  // Check both localStorage and sessionStorage for token
  const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors (expired token) + sliding token refresh
api.interceptors.response.use(
  (response) => {
    // Successful response means we can mark initialization as complete
    markInitialized();

    // Sliding token refresh: backend may have issued a fresh JWT in headers.
    // We persist it transparently — no UI flicker, no logout.
    const newToken = response.headers['x-new-token'];
    if (newToken) {
      const remember = localStorage.getItem('rememberMe') === 'true';
      const storage = remember ? localStorage : sessionStorage;
      storage.setItem('authToken', newToken);

      const expiresAt = response.headers['x-token-expires-at'];
      if (expiresAt) {
        storage.setItem('authTokenExpiresAt', expiresAt);
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('[API] Sliding token refresh applied');
      }
    }

    return response;
  },
  (error) => {
    // Only clear session and redirect if:
    // 1. It's a 401 error
    // 2. We're not in the initialization phase
    // 3. The error is not from auth endpoints (login, register, etc.)
    // 4. We're not in the process of logging out
    const isAuthEndpoint = error.config?.url?.includes('/auth/');
    
    if (error.response?.status === 401 && !isInitializing && !isAuthEndpoint && !isLoggingOut) {
      console.log('[API] 401 error - clearing session and redirecting to login');
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      localStorage.removeItem('rememberMe');
      sessionStorage.removeItem('authToken');
      sessionStorage.removeItem('user');
      // FIX 4: Redirect to login with expired flag so user sees feedback
      const isAuthRoute = window.location.pathname.includes('/login') ||
                          window.location.pathname.includes('/register');
      if (!isAuthRoute) {
        window.location.href = '/login?expired=true';
      }
    }
    return Promise.reject(error);
  }
);

// Start the initialization timeout
markInitialized();

// ==================== AUTH API ====================
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (name, email, password) => api.post('/auth/register', { name, email, password }),
  getMe: () => api.get('/auth/me'),
};

// ==================== COURSES API ====================
export const coursesAPI = {
  getAll: () => api.get('/courses'),
  getById: (courseId) => api.get(`/courses/${courseId}`),
};

// ==================== ENROLLMENT API ====================
export const enrollmentAPI = {
  enroll: (courseId) => api.post('/enrollment/enroll', { course_id: courseId }),
  getProgress: (courseId) => api.get(`/enrollment/progress/${courseId}`),
  completeLesson: (courseId, lessonId) => 
    api.post('/enrollment/complete-lesson', { course_id: courseId, lesson_id: lessonId }),
};

// ==================== COMMENTS API ====================
export const commentsAPI = {
  getByLesson: (lessonId) => api.get(`/comments/${lessonId}`),
  create: (lessonId, content) => api.post('/comments', { lesson_id: lessonId, content }),
};

// ==================== BLOG API ====================
export const blogAPI = {
  getAll: () => api.get('/blog'),
  getBySlug: (slug) => api.get(`/blog/${slug}`),
};

// ==================== ADMIN API ====================
export const adminAPI = {
  // Courses
  getCourses: () => api.get('/admin/courses'),
  createCourse: (data) => api.post('/admin/courses', data),
  updateCourse: (courseId, data) => api.put(`/admin/courses/${courseId}`, data),
  deleteCourse: (courseId) => api.delete(`/admin/courses/${courseId}`),
  
  // Modules
  getModules: (courseId) => api.get(`/admin/modules/${courseId}`),
  createModule: (data) => api.post('/admin/modules', data),
  updateModule: (moduleId, data) => api.put(`/admin/modules/${moduleId}`, data),
  deleteModule: (moduleId) => api.delete(`/admin/modules/${moduleId}`),
  
  // Lessons
  createLesson: (data) => api.post('/admin/lessons', data),
  updateLesson: (moduleId, lessonId, data) => api.put(`/admin/lessons/${moduleId}/${lessonId}`, data),
  deleteLesson: (moduleId, lessonId) => api.delete(`/admin/lessons/${moduleId}/${lessonId}`),
  
  // Users
  getUsers: () => api.get('/admin/users'),
  updateUserRole: (userId, role) => api.put(`/admin/users/${userId}/role?role=${role}`),
  
  // El Círculo Zoom Config
  getCirculoZoomConfig: () => api.get('/admin/circulo/zoom-config'),
  setCirculoZoomConfig: (data) => api.post('/admin/circulo/zoom-config', data),
};

// ==================== CMS API ====================
export const cmsAPI = {
  // Modules
  getModules: (courseId) => api.get(`/admin/cms/modules${courseId ? `?course_id=${courseId}` : ''}`),
  createModule: (data) => api.post('/admin/cms/modules', data),
  updateModule: (moduleId, data) => api.put(`/admin/cms/modules/${moduleId}`, data),
  deleteModule: (moduleId) => api.delete(`/admin/cms/modules/${moduleId}`),
  
  // Lessons
  getLessons: (courseId, moduleKey) => {
    let url = '/admin/cms/lessons';
    const params = [];
    if (courseId) params.push(`course_id=${courseId}`);
    if (moduleKey) params.push(`module_key=${moduleKey}`);
    if (params.length) url += `?${params.join('&')}`;
    return api.get(url);
  },
  getModuleLessons: (courseId, moduleKey) => api.get(`/admin/cms/lessons/${courseId}/${moduleKey}`),
  createLesson: (data) => api.post('/admin/cms/lessons', data),
  updateLesson: (lessonId, data) => api.put(`/admin/cms/lessons/${lessonId}`, data),
  deleteLesson: (lessonId) => api.delete(`/admin/cms/lessons/${lessonId}`),
  
  // Initialize structures
  initAyeka: () => api.post('/admin/cms/init-ayeka'),
  initCirculo: () => api.post('/admin/cms/init-circulo'),
  initRutaCritica: () => api.post('/admin/cms/init-ruta-critica'),
  
  // Public content
  getCourseContent: (courseId) => api.get(`/cms/course/${courseId}`),
};

// ==================== MEDIA API ====================
export const mediaAPI = {
  getFiles: (folder, type) => {
    let url = '/admin/media';
    const params = [];
    if (folder) params.push(`folder=${folder}`);
    if (type) params.push(`type=${type}`);
    if (params.length) url += `?${params.join('&')}`;
    return api.get(url);
  },
  createFile: (data) => api.post('/admin/media', data),
  updateFile: (fileId, data) => api.put(`/admin/media/${fileId}`, data),
  deleteFile: (fileId) => api.delete(`/admin/media/${fileId}`),
  createFolder: (name) => api.post(`/admin/media/folder?name=${encodeURIComponent(name)}`),
};

// ==================== ZOOM API ====================
export const zoomAPI = {
  getStatus: () => api.get('/zoom/status'),
  createMeeting: (topic, startTime, duration = 60) => 
    api.post(`/admin/zoom/create-meeting?topic=${encodeURIComponent(topic)}&start_time=${startTime}&duration=${duration}`),
  createRecurring: (topic, dayOfWeek, hour, minute = 0, duration = 90, courseId = null) => {
    let url = `/admin/zoom/create-recurring?topic=${encodeURIComponent(topic)}&day_of_week=${dayOfWeek}&hour=${hour}&minute=${minute}&duration=${duration}`;
    if (courseId) url += `&course_id=${courseId}`;
    return api.post(url);
  },
  getRecurring: (courseId) => api.get(`/admin/zoom/recurring/${courseId}`),
  getPendingRecordings: () => api.get('/admin/zoom/pending-recordings'),
  assignRecording: (recordingId, lessonId) => 
    api.post(`/admin/zoom/assign-recording?recording_id=${recordingId}&lesson_id=${lessonId}`),
};

// ==================== NEW YEAR. OLD YOU. API ====================
export const newYearAPI = {
  getCourse: () => api.get('/new-year/course'),
  enroll: () => api.post('/new-year/enroll'),
  signUrl: (path) => api.post('/new-year/sign-url', { path }),
  getExercise: (moduleKey) => api.get(`/new-year/exercise/${moduleKey}`),
  saveExercise: (moduleKey, payload) => api.put(`/new-year/exercise/${moduleKey}`, payload),
  completeExercise: (moduleKey) => api.post(`/new-year/exercise/${moduleKey}/complete`),
  adminExport: () => api.get('/admin/new-year/export-data'),
  adminEnrollments: () => api.get('/admin/new-year/enrollments'),
  adminExerciseProgress: (moduleKey = 'elul') => api.get(`/admin/new-year/exercise-progress?module_key=${moduleKey}`),
  patchSettings: (fields) => api.patch('/admin/new-year/settings', fields),
  patchClass: (moduleKey, fields) => api.patch(`/admin/new-year/class/${moduleKey}`, fields),
  uploadFile: (formData, { scope, fileType, moduleKey }) => {
    let url = `/admin/new-year/upload-file?scope=${scope}&file_type=${fileType}`;
    if (moduleKey) url += `&module_key=${moduleKey}`;
    return api.post(url, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  validateVimeo: (url) => api.post('/admin/vimeo/validate-url', { url }),
  // Donación (Stripe LIVE, pago único MXN)
  donationCheckout: (payload) => api.post('/new-year/donation/checkout', payload),
  donationStatus: (sessionId) => api.get(`/new-year/donation/status?session_id=${sessionId}`),
  guestDonationCheckout: (payload) => api.post('/new-year/donation/guest-checkout', payload),
  guestDonationStatus: (sessionId) => api.get(`/new-year/donation/guest-status?session_id=${sessionId}`),
  adminDonations: () => api.get('/admin/new-year/donations'),
};

export default api;
